/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { createSelector, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { fetchResource } from '../configuration/configuration.thunk';
import { ConfigResourceProps } from '../../enums/ConfigResourceProps';
import { fetchProcedureData } from './dashboard.thunk';
import { ThunkStatus } from '../../enums/ThunkStatus';
import { selectGlobalConfiguration, selectEnabledProcedureIds } from '../configuration/configuration.slice';
import { convertCategoriesToArray } from './dashboard.category.converter';

const tabStorageKey = 'dashboard.tab';

/* Initial State */
const initialState = {
  activeCategoryLabel: sessionStorage.getItem(tabStorageKey),
  categoryData: {},
};

/* Slice */
export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setActiveCategoryLabel(state, action) {
      sessionStorage.setItem(tabStorageKey, action.payload);
      state.activeCategoryLabel = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchResource.fulfilled, (state, action) => {
        if (!state.activeCategoryLabel && action.payload.name === ConfigResourceProps.GLOBAL_CONFIGURATION.name) {
          const dashboardCategories = convertCategoriesToArray(action.payload.value?.dashboardCategories || []);
          const firstCategory = dashboardCategories?.[0]?.label;
          sessionStorage.setItem(tabStorageKey, firstCategory);
          state.activeCategoryLabel = firstCategory;
        }
      })
      .addCase(fetchProcedureData.pending, (state, action) => {
        _.set(
          state,
          `categoryData.${action.payload?.procedureId || action.meta.arg.procedureId}.status`,
          ThunkStatus.LOADING,
        );
      })
      .addCase(fetchProcedureData.fulfilled, (state, action) => {
        _.set(state, `categoryData.${action.payload.procedureId}.status`, ThunkStatus.SUCCEEDED);
        _.set(state, `categoryData.${action.payload.procedureId}.procedureData`, action.payload.procedureData);
      })
      .addCase(fetchProcedureData.rejected, (state, action) => {
        _.set(
          state,
          `categoryData.${action.payload?.procedureId || action.meta.arg.procedureId}.status`,
          ThunkStatus.FAILED,
        );
      });
  },
});

/* Selectors */
const selectDashboard = state => state[dashboardSlice.name];

const selectActiveCategoryLabel = createSelector(selectDashboard, ({ activeCategoryLabel }) => activeCategoryLabel);

export const selectCategories = createSelector(
  selectEnabledProcedureIds,
  selectGlobalConfiguration,
  (enabledProcedureIds, globalConfig) =>
    globalConfig.dashboardCategories
      .map(dashboardCategory => ({
        ...dashboardCategory,
        procedureIds: dashboardCategory.procedureIds.filter(procedureId =>
          enabledProcedureIds.includes(procedureId.toString()),
        ),
      }))
      .filter(category => category.procedureIds.length > 0),
);

export const selectActiveCategory = createSelector(
  selectCategories,
  selectActiveCategoryLabel,
  (categories, activeCategoryLabel) => {
    return categories?.find(({ label }) => label === activeCategoryLabel) || categories[0];
  },
);
/**
 * Merge 'categoryData' (from state.dashboard) with additional info from 'procedures' (from state.configuration)
 *
 * Example result:
 {
    "NATPAT": {
        "total": 8,
        "data": [
            {
                "statusName": "DRAFT",
                "quantity": 5
            },
            {
                "statusName": "READY_TO_SIGN",
                "quantity": 0
            },
            {
                "statusName": "SIGNED",
                "quantity": 0
            }
        ],
        "id": "NATPAT",
        "label": "submission.NATPAT.title",
        "description": "submission.NATPAT.description",
        "signClient": "eOLFSignatureClient",
        "category": "national-procedure"
    },
    "EPVL": {
        "total": 3,
        "data": [
            {
                "statusName": "DRAFT",
                "quantity": 1
            },
            {
                "statusName": "READY_TO_SIGN",
                "quantity": 1
            },
            {
                "statusName": "SIGNED",
                "quantity": 0
            }
        ],
        "id": "EPVL",
        "label": "submission.epvl.title",
        "description": "submission.epvl.description",
        "signClient": "eOLFSignatureClient",
        "category": "ep-validation-procedure"
    }
}
 */

export const selectCategoryStatuses = createSelector(selectDashboard, ({ categoryData }) => {
  return Object.keys(categoryData).map(key => categoryData[key].status);
});

export const selectCategoryData = createSelector(
  selectDashboard,
  selectGlobalConfiguration,
  ({ categoryData }, { procedures }) =>
    Object.keys(categoryData).reduce(
      (accumulator, procedureId) => ({
        ...accumulator,
        [procedureId]: {
          ...categoryData[procedureId],
          ...procedures[procedureId],
        },
      }),
      {},
    ),
);

export const selectProcedureData = createSelector(
  (state, procedureId) => procedureId,
  selectDashboard,
  selectGlobalConfiguration,
  (procedureId, { categoryData }, { procedures }) => {
    return {
      status: ThunkStatus.IDLE,
      ...categoryData[procedureId],
      ...procedures[procedureId],
    };
  },
);

/* Actions */
export const { setActiveCategoryLabel } = dashboardSlice.actions;
